// src/components/Organizer/BankAccountSettings.tsx
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { getAppUrl } from "../../config";
import { apiClient } from "../../utils/api";

interface BankAccount {
	id: string;
	bank_code: string;
	bank_name: string;
	branch_code: string;
	branch_name: string;
	account_type: "ordinary" | "current" | "savings";
	account_type_label: string;
	account_number: string;
	account_holder_name: string;
	is_primary: boolean;
}

const BankAccountSettings: React.FC = () => {
	const [userBankAccounts, setUserBankAccounts] = useState<BankAccount[]>([]);
	const [selectedBankAccount, setSelectedBankAccount] = useState<BankAccount | null>(null);
	const [currentOrganizerBankAccount, setCurrentOrganizerBankAccount] = useState<BankAccount | null>(null);
	const [isLoading, setIsLoading] = useState(true);
	const [isSaving, setIsSaving] = useState(false);

	const fetchData = useCallback(async () => {
		setIsLoading(true);
		try {
			// Fetch user's bank accounts
			const accountsResponse = await apiClient.get("/bank-accounts");
			setUserBankAccounts(accountsResponse.data.bank_accounts || []);

			// Fetch organizer's current bank account
			const organizerBankResponse = await apiClient.get("/organizer/bank-account");
			setCurrentOrganizerBankAccount(organizerBankResponse.data.bankAccount);

			// Set selected bank account if organizer has one
			if (organizerBankResponse.data.bankAccount) {
				const matchingAccount = accountsResponse.data.bank_accounts.find(
					(acc: BankAccount) => acc.id === organizerBankResponse.data.bankAccount.id
				);
				if (matchingAccount) {
					setSelectedBankAccount(matchingAccount);
				}
			}
		} catch (error) {
			console.error("Failed to fetch bank accounts:", error);
			toast.error("銀行口座情報の取得に失敗しました");
		} finally {
			setIsLoading(false);
		}
	}, []);

	useEffect(() => {
		void fetchData();
	}, [fetchData]);

	const handleSave = async () => {
		if (!selectedBankAccount) {
			toast.error("銀行口座を選択してください");
			return;
		}

		setIsSaving(true);
		try {
			await apiClient.post("/organizer/bank-account", {
				bankAccountExternalId: selectedBankAccount.id,
			});
			toast.success("売上振込口座を設定しました");
			setCurrentOrganizerBankAccount(selectedBankAccount);
		} catch (error: any) {
			const errorMessage = error.response?.data?.message || "設定の保存に失敗しました";
			toast.error(errorMessage);
		} finally {
			setIsSaving(false);
		}
	};

	if (isLoading) {
		return (
			<div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
				<div className="flex items-center justify-center py-8">
					<div className="animate-spin h-8 w-8 border-4 border-violet-500 border-t-transparent rounded-full" />
				</div>
			</div>
		);
	}

	return (
		<div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 space-y-6">
			<div>
				<h3 className="text-lg font-semibold text-gray-900 dark:text-white">
					売上振込口座
				</h3>
				<p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
					チケット販売の売上を受け取る口座を設定してください
				</p>
			</div>

			{userBankAccounts.length === 0 ? (
				<div className="text-center py-8">
					<p className="text-gray-600 dark:text-gray-400">
						銀行口座が登録されていません
					</p>
					<p className="text-sm text-gray-500 dark:text-gray-500 mt-2">
						<a
							href={`${getAppUrl('accountApp')}/profile/bank-accounts`}
							target="_blank"
							rel="noopener noreferrer"
							className="text-violet-600 dark:text-violet-400 hover:underline"
						>
							アカウント管理ページ
						</a>
						から銀行口座を登録してください
					</p>
				</div>
			) : (
				<>
					<div className="space-y-3">
						{userBankAccounts.map((account) => (
							<label
								key={account.id}
								className={`flex items-start p-4 border-2 rounded-lg cursor-pointer transition-colors ${
									selectedBankAccount?.id === account.id
										? "border-violet-500 bg-violet-50 dark:bg-violet-900/20"
										: "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600"
								}`}
							>
								<input
									type="radio"
									name="bank-account"
									value={account.id}
									checked={selectedBankAccount?.id === account.id}
									onChange={() => setSelectedBankAccount(account)}
									className="mt-1 mr-3"
								/>
								<div className="flex-1">
									<div className="flex items-center gap-2">
										<span className="font-semibold text-gray-900 dark:text-white">
											{account.bank_name} {account.branch_name}支店
										</span>
										{account.is_primary && (
											<span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
												優先口座
											</span>
										)}
										{currentOrganizerBankAccount?.id === account.id && (
											<span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
												現在の振込先
											</span>
										)}
									</div>
									<p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
										{account.account_type_label} {account.account_number}
									</p>
									<p className="text-sm text-gray-600 dark:text-gray-400">
										{account.account_holder_name}
									</p>
									<p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
										銀行コード: {account.bank_code} / 支店コード:{" "}
										{account.branch_code}
									</p>
								</div>
							</label>
						))}
					</div>

					<div className="flex justify-end">
						<button
							onClick={handleSave}
							disabled={
								isSaving ||
								!selectedBankAccount ||
								currentOrganizerBankAccount?.id === selectedBankAccount?.id
							}
							className="px-6 py-2 bg-gradient-to-r from-violet-500 to-purple-600 text-white rounded-lg font-medium hover:from-violet-600 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
						>
							{isSaving ? "保存中..." : "振込口座を設定"}
						</button>
					</div>
				</>
			)}
		</div>
	);
};

export default BankAccountSettings;
